import { GroupedFormEditor } from '@/components/config-editor/components/GroupedFormEditor';
import { Button } from '@/components/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fields, widgets } from '@/data/rjsf';
import { Code, Eye, RotateCcw, Sparkles } from 'lucide-react';
import { useState } from 'react';

// Demo Schema showcasing all widgets
const demoSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  title: 'Config Editor Demo',
  properties: {
    meta: {
      type: 'object',
      title: 'Metadata',
      properties: {
        name: {
          type: 'string',
          title: 'Configuration Name',
          description: 'A unique name for this configuration',
        },
        version: {
          type: 'string',
          title: 'Version',
          default: '1.0.0',
        },
      },
    },
    theme: {
      type: 'object',
      title: 'Theme Colors',
      properties: {
        primary: {
          type: 'string',
          title: 'Primary Color',
          default: '#3B82F6',
        },
        secondary: {
          type: 'string',
          title: 'Secondary Color',
          default: '#F3F4F6',
        },
        accent: {
          type: 'string',
          title: 'Accent Color',
          default: '#10B981',
        },
      },
    },
    layout: {
      type: 'object',
      title: 'Layout Options',
      properties: {
        type: {
          type: 'string',
          title: 'Layout Type',
          enum: ['single-column', 'two-column', 'sidebar', 'full-width'],
          default: 'single-column',
        },
        showHeader: {
          type: 'boolean',
          title: 'Show Header',
          description: 'Display the page header section',
          default: true,
        },
        showFooter: {
          type: 'boolean',
          title: 'Show Footer',
          description: 'Display the page footer section',
          default: true,
        },
        showSidebar: {
          type: 'boolean',
          title: 'Show Sidebar',
          description: 'Display the sidebar navigation',
          default: false,
        },
      },
    },
    features: {
      type: 'object',
      title: 'Feature Toggles',
      properties: {
        enableAnalytics: {
          type: 'boolean',
          title: 'Enable Analytics',
          description: 'Track user interactions and page views',
          default: true,
        },
        enableNotifications: {
          type: 'boolean',
          title: 'Enable Notifications',
          description: 'Show push notifications to users',
          default: false,
        },
        enableDarkMode: {
          type: 'boolean',
          title: 'Enable Dark Mode',
          description: 'Allow users to switch to dark theme',
          default: true,
        },
        enableExports: {
          type: 'boolean',
          title: 'Enable Exports',
          description: 'Allow data export functionality',
          default: false,
        },
      },
    },
    benefits: {
      type: 'array',
      title: 'Product Benefits',
      items: {
        type: 'object',
        properties: {
          icon: {
            type: 'string',
            title: 'Icon',
            default: 'Check',
          },
          title: {
            type: 'object',
            title: 'Title',
            format: 'translation',
          },
          description: {
            type: 'object',
            title: 'Description',
            format: 'translation',
          },
        },
      },
    },
    texts: {
      type: 'object',
      title: 'UI Texts',
      properties: {
        heroTitle: {
          type: 'object',
          title: 'Hero Title',
          format: 'translation',
        },
        heroSubtitle: {
          type: 'object',
          title: 'Hero Subtitle',
          format: 'translation',
        },
        ctaButton: {
          type: 'object',
          title: 'CTA Button Text',
          format: 'translation',
        },
      },
    },
  },
};

const demoUiSchema = {
  'ui:grouping': {
    tabs: [
      { id: 'design', title: '🎨 Design', order: 1 },
      { id: 'content', title: '📝 Content', order: 2 },
      { id: 'settings', title: '⚙️ Settings', order: 3 },
    ],
    sections: [
      { id: 'meta', title: '📋 Metadata', tabId: 'settings', order: 1 },
      { id: 'theme', title: '🎨 Theme Colors', tabId: 'design', order: 1 },
      { id: 'layout', title: '📐 Layout Options', tabId: 'design', order: 2 },
      { id: 'features', title: '🔧 Feature Toggles', tabId: 'settings', order: 2 },
      { id: 'benefits', title: '✨ Benefits', tabId: 'content', order: 1 },
      { id: 'texts', title: '🌍 UI Texts', tabId: 'content', order: 2 },
    ],
  },
  meta: {
    'ui:tab': 'settings',
    'ui:section': 'meta',
  },
  theme: {
    'ui:tab': 'design',
    'ui:section': 'theme',
    'ui:options': { grid: 3 },
    primary: { 'ui:widget': 'color' },
    secondary: { 'ui:widget': 'color' },
    accent: { 'ui:widget': 'color' },
  },
  layout: {
    'ui:tab': 'design',
    'ui:section': 'layout',
    'ui:options': { grid: 2 },
    showHeader: { 'ui:widget': 'toggleSwitch' },
    showFooter: { 'ui:widget': 'toggleSwitch' },
    showSidebar: { 'ui:widget': 'toggleSwitch' },
  },
  features: {
    'ui:tab': 'settings',
    'ui:section': 'features',
    'ui:options': { grid: 2 },
    enableAnalytics: { 'ui:widget': 'toggleSwitch' },
    enableNotifications: { 'ui:widget': 'toggleSwitch' },
    enableDarkMode: { 'ui:widget': 'toggleSwitch' },
    enableExports: { 'ui:widget': 'toggleSwitch' },
  },
  benefits: {
    'ui:tab': 'content',
    'ui:section': 'benefits',
    items: {
      icon: { 'ui:widget': 'iconSelector' },
      title: { 'ui:field': 'translation' },
      description: { 'ui:field': 'translation' },
    },
  },
  texts: {
    'ui:tab': 'content',
    'ui:section': 'texts',
    heroTitle: { 'ui:field': 'translation' },
    heroSubtitle: { 'ui:field': 'translation' },
    ctaButton: { 'ui:field': 'translation' },
  },
};

const demoFormData = {
  meta: {
    name: 'My Awesome Config',
    version: '1.0.0',
  },
  theme: {
    primary: '#3B82F6',
    secondary: '#F3F4F6',
    accent: '#10B981',
  },
  layout: {
    type: 'single-column',
    showHeader: true,
    showFooter: true,
    showSidebar: false,
  },
  features: {
    enableAnalytics: true,
    enableNotifications: false,
    enableDarkMode: true,
    enableExports: false,
  },
  benefits: [
    {
      icon: 'Zap',
      title: { en: 'Lightning Fast', fr: 'Ultra Rapide' },
      description: { en: 'Experience blazing fast performance', fr: 'Performance ultra rapide' },
    },
    {
      icon: 'Shield',
      title: { en: 'Secure & Safe', fr: 'Sécurisé' },
      description: { en: 'Your data is protected', fr: 'Vos données sont protégées' },
    },
    {
      icon: 'Heart',
      title: { en: 'Made with Love', fr: 'Fait avec Amour' },
      description: { en: 'Crafted with care', fr: 'Créé avec soin' },
    },
  ],
  texts: {
    heroTitle: { en: 'Welcome to Our Platform', fr: 'Bienvenue sur Notre Plateforme' },
    heroSubtitle: { en: 'The best solution for your needs', fr: 'La meilleure solution pour vos besoins' },
    ctaButton: { en: 'Get Started', fr: 'Commencer' },
  },
  languages: ['en', 'fr'],
};

// Preset templates
const TEMPLATES = {
  demo: {
    name: '🎮 Demo (All Widgets)',
    schema: demoSchema,
    uiSchema: demoUiSchema,
    formData: demoFormData,
  },
  minimal: {
    name: '📦 Minimal',
    schema: {
      type: 'object',
      properties: {
        name: { type: 'string', title: 'Name' },
        enabled: { type: 'boolean', title: 'Enabled', default: true },
      },
    },
    uiSchema: {
      enabled: { 'ui:widget': 'toggleSwitch' },
    },
    formData: { name: 'Test', enabled: true },
  },
  icons: {
    name: '🎨 Icon Picker',
    schema: {
      type: 'object',
      properties: {
        items: {
          type: 'array',
          title: 'Items with Icons',
          items: {
            type: 'object',
            properties: {
              icon: {
                type: 'string',
                title: 'Icon',
              },
              label: { type: 'string', title: 'Label' },
            },
          },
        },
      },
    },
    uiSchema: {
      items: {
        items: {
          icon: { 'ui:widget': 'iconSelector' },
        },
      },
    },
    formData: {
      items: [
        { icon: 'Star', label: 'Featured' },
        { icon: 'Zap', label: 'Fast' },
      ],
    },
  },
  translations: {
    name: '🌍 Translations',
    schema: {
      type: 'object',
      properties: {
        content: {
          type: 'object',
          title: 'Content',
          properties: {
            title: { type: 'object', title: 'Title', format: 'translation' },
            description: { type: 'object', title: 'Description', format: 'translation' },
            buttonText: { type: 'object', title: 'Button Text', format: 'translation' },
          },
        },
      },
    },
    uiSchema: {
      content: {
        title: { 'ui:field': 'translation' },
        description: { 'ui:field': 'translation' },
        buttonText: { 'ui:field': 'translation' },
      },
    },
    formData: {
      content: {
        title: { en: 'Hello World', fr: 'Bonjour le Monde', es: 'Hola Mundo' },
        description: { en: 'Welcome!', fr: 'Bienvenue!', es: '¡Bienvenido!' },
        buttonText: { en: 'Click Me', fr: 'Cliquez-moi', es: 'Haz clic' },
      },
      languages: ['en', 'fr', 'es'],
    },
  },
  conditional: {
    name: '🔄 Conditional Fields',
    schema: {
      type: 'object',
      title: '🔄 Conditional Fields',
      properties: {
        animal: {
          type: 'string',
          title: '🐾 Animal',
          enum: ['🐱 Cat', '🐟 Fish'],
        },
      },
      allOf: [
        {
          if: {
            properties: {
              animal: {
                const: '🐱 Cat',
              },
            },
          },
          then: {
            properties: {
              food: {
                type: 'string',
                title: '🍽️ Food',
                enum: ['🥩 meat', '🌱 grass', '🐟 fish'],
              },
            },
          },
        },
        {
          if: {
            properties: {
              animal: {
                const: '🐟 Fish',
              },
            },
          },
          then: {
            properties: {
              food: {
                type: 'string',
                title: '🍽️ Food',
                enum: ['🦗 insect', '🪱 worms'],
              },
              water: {
                type: 'string',
                title: '💧 Water',
                enum: ['🏞️ lake', '🌊 sea'],
              },
            },
          },
        },
      ],
    },
    uiSchema: {},
    formData: {
      animal: '🐱 Cat',
      food: '🥩 meat',
    },
  },
  cardNetworkSelector: {
    name: '💳 Card Network Selector',
    schema: {
      type: 'object',
      properties: {
        network: {
          type: 'object',
          title: 'Card Network',
          description: 'Select a predefined card network or create a custom one',
        },
      },
    },
    uiSchema: {
      network: { 'ui:field': 'cardNetworkSelector' },
    },
    formData: {
      network: { id: 'visa' },
    },
  },
  media: {
    name: '🎬 Media (Image & Video)',
    schema: {
      type: 'object',
      properties: {
        branding: {
          type: 'object',
          title: 'Branding',
          properties: {
            logo: {
              type: 'string',
              title: 'Logo Image',
              description: 'Upload or enter URL for logo image',
            },
            heroImage: {
              type: 'string',
              title: 'Hero Image',
              description: 'Upload or enter URL for hero background image',
            },
          },
        },
        media: {
          type: 'object',
          title: 'Media Content',
          properties: {
            promoVideo: {
              type: 'string',
              title: 'Promotional Video',
              description: 'Upload or enter URL for promotional video',
            },
            tutorialVideo: {
              type: 'string',
              title: 'Tutorial Video',
              description: 'Upload or enter URL for tutorial/demo video',
            },
            backgroundVideo: {
              type: 'string',
              title: 'Background Video',
              description: 'Upload or enter URL for background loop video',
            },
          },
        },
      },
    },
    uiSchema: {
      branding: {
        logo: { 'ui:widget': 'image' },
        heroImage: { 'ui:widget': 'image' },
      },
      media: {
        promoVideo: { 'ui:widget': 'video' },
        tutorialVideo: { 'ui:widget': 'video' },
        backgroundVideo: { 'ui:widget': 'video' },
      },
    },
    formData: {
      branding: {
        logo: '',
        heroImage: '',
      },
      media: {
        promoVideo: '',
        tutorialVideo: '',
        backgroundVideo: '',
      },
    },
  },
};

// Default initial values
const defaultSchema = demoSchema;
const defaultUiSchema = demoUiSchema;
const defaultFormData = demoFormData;

/**
 * Recursively removes all 'x-pages' properties from a uiSchema object
 */
function removeXPagesFromUiSchema(uiSchema: any): any {
  if (!uiSchema || typeof uiSchema !== 'object') {
    return uiSchema;
  }

  // Handle arrays
  if (Array.isArray(uiSchema)) {
    return uiSchema.map((item) => removeXPagesFromUiSchema(item));
  }

  // Create a new object without 'x-pages' and recursively process nested objects
  const cleaned: any = {};
  for (const [key, value] of Object.entries(uiSchema)) {
    if (key === 'x-pages') {
      // Skip x-pages property
      continue;
    }
    cleaned[key] = removeXPagesFromUiSchema(value);
  }

  return cleaned;
}

interface RJSFPlaygroundProps {
  storeId?: string;
}

export function RJSFPlayground({ storeId }: RJSFPlaygroundProps) {
  const [activeTab, setActiveTab] = useState<'schema' | 'uischema' | 'data'>('data');
  const cleanedDefaultUiSchema = removeXPagesFromUiSchema(defaultUiSchema);
  const [schemaText, setSchemaText] = useState(JSON.stringify(defaultSchema, null, 2));
  const [uiSchemaText, setUiSchemaText] = useState(JSON.stringify(cleanedDefaultUiSchema, null, 2));
  const [formDataText, setFormDataText] = useState(JSON.stringify(defaultFormData, null, 2));

  const [schema, setSchema] = useState<any>(defaultSchema);
  const [uiSchema, setUiSchema] = useState<any>(cleanedDefaultUiSchema);
  const [formData, setFormData] = useState<any>(defaultFormData);

  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [uiSchemaError, setUiSchemaError] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);

  // RJSF widgets and fields

  const loadTemplate = (templateId: string) => {
    const template = TEMPLATES[templateId as keyof typeof TEMPLATES];
    if (template) {
      const cleanedUiSchema = removeXPagesFromUiSchema(template.uiSchema);
      setSchemaText(JSON.stringify(template.schema, null, 2));
      setUiSchemaText(JSON.stringify(cleanedUiSchema, null, 2));
      setFormDataText(JSON.stringify(template.formData, null, 2));
      setSchema(template.schema);
      setUiSchema(cleanedUiSchema);
      setFormData(template.formData);
      setSchemaError(null);
      setUiSchemaError(null);
      setDataError(null);
    }
  };

  const handleSchemaChange = (value: string) => {
    setSchemaText(value);
    try {
      const parsed = JSON.parse(value);
      setSchema(parsed);
      setSchemaError(null);
    } catch (error) {
      setSchemaError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const handleUiSchemaChange = (value: string) => {
    setUiSchemaText(value);
    try {
      const parsed = JSON.parse(value);
      const cleaned = removeXPagesFromUiSchema(parsed);
      setUiSchema(cleaned);
      setUiSchemaError(null);
    } catch (error) {
      setUiSchemaError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const handleFormDataChange = (value: string) => {
    setFormDataText(value);
    try {
      const parsed = JSON.parse(value);
      setFormData(parsed);
      setDataError(null);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const handleFormChange = (data: any) => {
    setFormData(data.formData);
    setFormDataText(JSON.stringify(data.formData, null, 2));
  };

  const handleReset = () => {
    const cleanedDefaultUiSchema = removeXPagesFromUiSchema(defaultUiSchema);
    setSchemaText(JSON.stringify(defaultSchema, null, 2));
    setUiSchemaText(JSON.stringify(cleanedDefaultUiSchema, null, 2));
    setFormDataText(JSON.stringify(defaultFormData, null, 2));
    setSchema(defaultSchema);
    setUiSchema(cleanedDefaultUiSchema);
    setFormData(defaultFormData);
    setSchemaError(null);
    setUiSchemaError(null);
    setDataError(null);
  };

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold">Config Editor Playground</h1>
          <code className="bg-muted text-muted-foreground rounded px-2 py-1 font-mono text-xs">
            Test widgets & schemas
          </code>
        </div>
        <div className="flex items-center gap-3">
          {/* Template Selector */}
          <div className="flex items-center gap-2">
            <Sparkles className="text-muted-foreground h-4 w-4" />
            <Select onValueChange={loadTemplate}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="Load Template..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TEMPLATES).map(([id, template]) => (
                  <SelectItem key={id} value={id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Main Content - Split View */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Left Panel - Editors */}
        <ResizablePanel defaultSize={40} minSize={25} maxSize={60}>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as any)}
            className="flex h-full flex-col"
          >
            <div className="border-border flex h-full flex-col border-r">
              <div className="border-border border-b px-4 py-2">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="data" className="gap-1.5 text-xs">
                    <Eye className="h-3.5 w-3.5" />
                    Form Data
                  </TabsTrigger>
                  <TabsTrigger value="schema" className="gap-1.5 text-xs">
                    <Code className="h-3.5 w-3.5" />
                    Schema
                  </TabsTrigger>
                  <TabsTrigger value="uischema" className="gap-1.5 text-xs">
                    <Code className="h-3.5 w-3.5" />
                    UI Schema
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 overflow-auto p-4">
                <TabsContent value="data" className="mt-0 h-full">
                  <div className="space-y-2">
                    {dataError && (
                      <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/20">
                        <strong>Error:</strong> {dataError}
                      </div>
                    )}
                    <textarea
                      value={formDataText}
                      onChange={(e) => handleFormDataChange(e.target.value)}
                      className="bg-muted focus:ring-primary h-[calc(100vh-220px)] w-full resize-none rounded-md border p-4 font-mono text-xs focus:outline-none focus:ring-2"
                      spellCheck={false}
                      placeholder="Enter form data JSON..."
                    />
                  </div>
                </TabsContent>

                <TabsContent value="schema" className="mt-0 h-full">
                  <div className="space-y-2">
                    {schemaError && (
                      <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/20">
                        <strong>Error:</strong> {schemaError}
                      </div>
                    )}
                    <textarea
                      value={schemaText}
                      onChange={(e) => handleSchemaChange(e.target.value)}
                      className="bg-muted focus:ring-primary h-[calc(100vh-220px)] w-full resize-none rounded-md border p-4 font-mono text-xs focus:outline-none focus:ring-2"
                      spellCheck={false}
                      placeholder="Enter JSON schema..."
                    />
                  </div>
                </TabsContent>

                <TabsContent value="uischema" className="mt-0 h-full">
                  <div className="space-y-2">
                    {uiSchemaError && (
                      <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/20">
                        <strong>Error:</strong> {uiSchemaError}
                      </div>
                    )}
                    <textarea
                      value={uiSchemaText}
                      onChange={(e) => handleUiSchemaChange(e.target.value)}
                      className="bg-muted focus:ring-primary h-[calc(100vh-220px)] w-full resize-none rounded-md border p-4 font-mono text-xs focus:outline-none focus:ring-2"
                      spellCheck={false}
                      placeholder="Enter UI schema..."
                    />
                  </div>
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right Panel - Live Preview */}
        <ResizablePanel defaultSize={60} minSize={40}>
          <div className="flex h-full flex-col">
            <div className="border-border flex items-center justify-between border-b px-4 py-2">
              <h3 className="text-sm font-semibold">Live Preview</h3>
              <div className="flex items-center gap-2">
                {schemaError && <span className="text-xs text-red-600">Schema Error</span>}
                {uiSchemaError && <span className="text-xs text-red-600">UI Schema Error</span>}
                {!schemaError && !uiSchemaError && <span className="text-xs text-green-600">✓ Valid</span>}
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {!schemaError && !uiSchemaError ? (
                <GroupedFormEditor
                  schema={schema}
                  uiSchema={removeXPagesFromUiSchema(uiSchema)}
                  formData={formData}
                  onChange={handleFormChange}
                  widgets={widgets}
                  fields={fields}
                  storeId={storeId}
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <div className="text-muted-foreground text-center text-sm">
                    <p className="mb-2">Cannot render form</p>
                    <p className="text-xs">Fix the JSON errors in Schema or UI Schema</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Footer - Info */}
      <div className="border-border bg-muted/50 flex items-center justify-between border-t px-4 py-2">
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground">
            Edit the schema, UI schema, or form data on the left to see changes in real-time
          </span>
        </div>
        <div className="text-muted-foreground text-xs">Press Ctrl/Cmd + K to format JSON</div>
      </div>
    </div>
  );
}
